/**
 * @file GameError.ts
 * @role Structured error type + code taxonomy used across the app. A single error
 *       shape lets the UI map any failure to a recoverable/retry panel consistently.
 * @owns The GameErrorCode union and the GameError class.
 * @reads Nothing.
 * @neverDoes Calls backend. Touches Pixi. Logs analytics.
 * @invariants 21 (errors route reels to a safe terminal state — enforced by callers).
 * @see docs/INVARIANTS.md
 */

/** The closed taxonomy of failures the game knows how to present. */
export type GameErrorCode =
  | 'CONFIG_LOAD_FAILED'
  | 'ASSET_BUNDLE_FAILED'
  | 'SESSION_CREATE_FAILED'
  | 'SPIN_REQUEST_FAILED'
  | 'SPIN_RESPONSE_INVALID'
  | 'INSUFFICIENT_BALANCE'
  | 'SESSION_EXPIRED'
  | 'NETWORK_TIMEOUT'
  | 'ROUND_STATUS_UNKNOWN'
  | 'UNKNOWN_ERROR';

export class GameError extends Error {
  readonly code: GameErrorCode;
  /** Whether the user can sensibly retry the failed operation. */
  readonly recoverable: boolean;
  readonly cause?: unknown;

  constructor(
    code: GameErrorCode,
    message: string,
    options?: { recoverable?: boolean; cause?: unknown },
  ) {
    super(message);
    this.name = 'GameError';
    this.code = code;
    this.recoverable = options?.recoverable ?? false;
    if (options?.cause !== undefined) {
      this.cause = options.cause;
    }
  }
}
